import React from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Badge } from '@mui/material';
import { Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import { useMyShift } from '../hook/useMyShift';
import { CalendarGrid } from '../components/CalendarGrid';
import { MonthNavigation } from '../components/MonthNavigation';
import { RecruitmentPeriodDisplay } from '../components/RecruitmentPeriodDisplay';
import { ShiftSubmissionForm } from '../components/ShiftSubmissionForm';
import { ConfirmDialog } from '../components/ConfirmDialog';

/**
 * MyShiftコンテナコンポーネント
 */
export const MyShiftContainer: React.FC = () => {
  const {
    // State
    shifts,
    recruitmentPeriod,
    recruitmentPeriodError,
    currentMonth,
    isLoading,
    error,
    isRetrying,
    monthlyStatusPhase,

    // Pending Changes State
    pendingChanges,
    hasPendingChanges,
    isSaving,

    // Form State
    isFormOpen,
    selectedDate,
    selectedSlots,
    extraShifts,
    formEditable,
    isSubmitting,

    // Confirm Dialog State
    confirmDialogType,

    // Navigation
    handlePreviousMonth,
    handleNextMonth,

    // Form Actions
    openShiftForm,
    closeShiftForm,
    updateSlotSelection,

    // Submit Actions
    handleConfirmClick,
    confirmLocalSave,
    cancelConfirm,

    // Save Actions
    saveAllChanges,

    // Retry
    retry,
  } = useMyShift();

  const isFixedRecruiting = monthlyStatusPhase === 'fixed_recruiting';

  // ローディング表示
  if (isLoading && !isRetrying) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* ヘッダー */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          マイシフト
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* 保存ボタン */}
          <Badge
            badgeContent={pendingChanges.length}
            color="error"
            invisible={!hasPendingChanges}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={saveAllChanges}
              disabled={!hasPendingChanges || isSaving}
            >
              {isSaving ? '保存中...' : '保存'}
            </Button>
          </Badge>
        </Box>
      </Box>

      {/* 未保存の変更がある場合の警告 */}
      {hasPendingChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {pendingChanges.length}件の未保存の変更があります。「保存」ボタンをクリックして変更を保存してください。
        </Alert>
      )}

      {/* エラー表示 */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={retry}
              disabled={isRetrying}
            >
              {isRetrying ? '再試行中...' : '再読み込み'}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* 固定シフト募集中の案内 */}
      {isFixedRecruiting && (
        <Alert severity="info" sx={{ mb: 2 }}>
          固定シフト募集中です。カレンダーの日付を押して、勤務できない枠に「不可」を付けてください。
        </Alert>
      )}

      {/* 募集期間表示 */}
      <RecruitmentPeriodDisplay
        period={recruitmentPeriod}
        error={recruitmentPeriodError || undefined}
      />

      <Box sx={{ overflow: 'hidden', border: '1px solid #dedbd7', borderRadius: '12px', bgcolor: '#fff', boxShadow: '0 1px 2px #2725220a' }}>
        <MonthNavigation currentMonth={currentMonth} onPrevious={handlePreviousMonth} onNext={handleNextMonth} isLoading={isLoading} />
        <CalendarGrid currentMonth={currentMonth} shifts={shifts} onDayClick={openShiftForm} />
      </Box>

      {/* 日別フォーム */}
      <ShiftSubmissionForm
        open={isFormOpen}
        selectedDate={selectedDate || undefined}
        phase={monthlyStatusPhase}
        slots={selectedSlots}
        extraShifts={extraShifts}
        editable={formEditable}
        onSlotChange={updateSlotSelection}
        onSubmit={handleConfirmClick}
        onCancel={closeShiftForm}
        isSubmitting={isSubmitting}
      />

      {/* 確定確認ダイアログ */}
      <ConfirmDialog
        open={confirmDialogType === 'submit'}
        title={isFixedRecruiting ? '勤務不可の確定' : 'シフト希望の確定'}
        message={
          isFixedRecruiting
            ? '不可にした枠は「保存」ボタンを押した時点でカレンダーから外れます。保存するまでは、日付を開き直せば不可を取り消せます。'
            : 'この内容で確定してよろしいですか？（「保存」ボタンを押すまでデータベースには反映されません）'
        }
        confirmText="確定"
        cancelText="キャンセル"
        onConfirm={confirmLocalSave}
        onCancel={cancelConfirm}
        isLoading={isSubmitting}
      />
    </Box>
  );
};

export default MyShiftContainer;
